import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, MapPin, Heart } from 'lucide-react';
import { api, pickLocale } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

export default function ProjectDetail() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ['project', slug], queryFn: async () => (await api.get(`/projects/${slug}`)).data });
  useSEO({ title: p ? pickLocale(p.title, lang) : 'Programme', description: p ? pickLocale(p.summary, lang) : undefined });

  if (isLoading) return <div className="min-h-[60vh] flex items-center justify-center text-ukgm-slate">{t('common.loading')}</div>;
  if (isError || !p) return <div className="min-h-[60vh] flex items-center justify-center text-ukgm-slate">Not found</div>;

  return (
    <div data-testid="project-detail-page">
      <section className="hero-blue text-white pt-14 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/projects" className="inline-flex items-center gap-2 text-ukgm-gold text-sm mb-4"><ArrowLeft className="w-4 h-4" /> {t('projects.cta_all')}</Link>
          <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t(`projects.categories.${p.category}`)}</div>
          <h1 className="mt-2 font-serif text-4xl md:text-6xl font-semibold leading-tight">{pickLocale(p.title, lang)}</h1>
          {p.location && (
            <div className="mt-4 inline-flex items-center gap-2 text-white/80 text-sm border border-ukgm-gold/30 rounded-full px-4 py-1.5">
              <MapPin className="w-4 h-4 text-ukgm-gold" /> {p.location}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <p className="text-lg text-ukgm-slate leading-relaxed">{pickLocale(p.summary, lang)}</p>
          <div className="gold-divider my-8" />
          <p className="text-ukgm-charcoal leading-relaxed whitespace-pre-line">{pickLocale(p.description, lang)}</p>
        </div>
        <aside className="lg:col-span-4">
          <div className="sticky top-24 bg-white rounded-2xl border border-ukgm-gold/25 p-6">
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">Support this programme</div>
            <p className="mt-3 text-sm text-ukgm-slate">Donations to Universal Krishna Gaukul Mission are 80G tax-exempt in India.</p>
            <button onClick={() => document.querySelector('[data-testid="donate-header-btn"]')?.click() || document.querySelector('[data-testid="donate-fab"]')?.click()}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-6 py-3 rounded-full hover:bg-ukgm-goldDark" data-testid="project-donate-btn">
              <Heart className="w-4 h-4" fill="currentColor" /> {t('hero.cta_donate')}
            </button>
            <div className="mt-4 pt-4 border-t border-ukgm-gold/20 text-xs text-ukgm-slate space-y-1">
              <div><b>Bank:</b> HDFC Bank</div>
              <div><b>A/C:</b> 50200117192679</div>
              <div><b>Title:</b> UNIVERSAL KRISHNA GAUKUL MISSION</div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
